import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslocoService } from '@jsverse/transloco';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { NotificationTypes } from 'src/app/app.enums';
import { ResponseObject } from 'src/app/Services/auth.service';
import { PlatformCredentialService } from 'src/app/Services/platform-credential.service';
import {
  AiFunctionView,
  AiHealthReport,
  AiProbeResult,
  AiSpendReport,
  AiProviderCredential,
  PlatformCredentialAudit,
  PlatformCredentialsResponse,
  SaveCredentialRequest,
  SaveFunctionBindingRequest,
  TestCredentialRequest
} from 'src/app/models/platform-credential.model';

/**
 * API Keys — the operator's surface for the platform's AI provider credentials.
 *
 * <p>The screen never displays a key. A stored credential shows as its last four characters and
 * nothing more, because the API has no route that returns a value. Typing into the box and saving
 * REPLACES the credential; there is no edit-in-place, since we cannot show what is there to edit.</p>
 *
 * <p>"Test" behaves differently depending on whether the box has been typed into: with a candidate
 * value it probes that key before you commit to it, and with an empty box it probes whatever is
 * actually in force. Both questions matter, and conflating them hides which key was proven.</p>
 */
@Component({
  selector: 'app-api-keys',
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.scss'],
  standalone: false
})
export class ApiKeysComponent implements OnInit {
  public providers: AiProviderCredential[] = [];
  public encryptionConfigured: boolean = true;
  public dataLoaded: boolean = false;

  /** Candidate values, keyed by CredentialKey. Never populated from the server. */
  public candidates: { [credentialKey: string]: string } = {};

  public probeResults: { [providerKey: string]: AiProbeResult } = {};
  public testing: { [providerKey: string]: boolean } = {};

  /** keys | functions — which tab is showing. */
  public activeTab: string = 'keys';

  public functions: AiFunctionView[] = [];
  public functionsLoaded: boolean = false;

  public health: AiHealthReport | null = null;
  public spend: AiSpendReport | null = null;
  public healthLoaded: boolean = false;
  public checkingHealth: boolean = false;

  public auditTrail: PlatformCredentialAudit[] = [];
  public auditVisible: boolean = false;

  constructor(
    private platformCredentialService: PlatformCredentialService,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.loadCredentials();
  }

  public loadCredentials(): void {
    this.ngxSpinnerService.show();

    this.platformCredentialService.fetchCredentials().subscribe({
      next: (response: ResponseObject) => {
        this.ngxSpinnerService.hide();

        // HTTP 200 with Success:false is the normal failure mode on this API.
        if (!response.Success) {
          this.notify(response.ResponseMessage || this.t('apiKeys.loadFailed', 'Could not load the API keys.'), NotificationTypes.Error);
          return;
        }

        const data = response.ResponseData as PlatformCredentialsResponse;
        this.providers = data?.Providers ?? [];
        this.encryptionConfigured = data?.EncryptionConfigured !== false;
        this.dataLoaded = true;
      },
      error: (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error while fetching platform credentials', error);
        this.notify(this.t('apiKeys.loadFailed', 'Could not load the API keys.'), NotificationTypes.Error);
      }
    });
  }

  public hasCandidate(provider: AiProviderCredential): boolean {
    const value = this.candidates[provider.CredentialKey];
    return !!value && value.trim().length > 0;
  }

  public onSave(provider: AiProviderCredential): void {
    if (!this.hasCandidate(provider)) {
      return;
    }

    const request = new SaveCredentialRequest();
    request.CredentialKey = provider.CredentialKey;
    request.Value = this.candidates[provider.CredentialKey].trim();

    this.ngxSpinnerService.show();

    this.platformCredentialService.saveCredential(request).subscribe({
      next: (response: ResponseObject) => {
        this.ngxSpinnerService.hide();

        if (!response.Success) {
          this.notify(response.ResponseMessage || this.t('apiKeys.saveFailed', 'The key was not saved.'), NotificationTypes.Error);
          return;
        }

        // Clear the box immediately — leaving a secret sitting in the DOM after it has been stored
        // serves no purpose and survives into screenshots and screen shares.
        this.candidates[provider.CredentialKey] = '';
        this.probeResults[provider.Key] = null as unknown as AiProbeResult;

        this.notify(this.t('apiKeys.saved', 'Key saved.'), NotificationTypes.Success);
        this.loadCredentials();
      },
      error: (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error while saving a platform credential', error);
        this.notify(this.t('apiKeys.saveFailed', 'The key was not saved.'), NotificationTypes.Error);
      }
    });
  }

  public onRemove(provider: AiProviderCredential): void {
    this.ngxSpinnerService.show();

    this.platformCredentialService.removeCredential(provider.CredentialKey).subscribe({
      next: (response: ResponseObject) => {
        this.ngxSpinnerService.hide();

        if (!response.Success) {
          this.notify(response.ResponseMessage || this.t('apiKeys.removeFailed', 'The key was not removed.'), NotificationTypes.Error);
          return;
        }

        this.notify(this.t('apiKeys.removed', 'Key removed.'), NotificationTypes.Success);
        this.loadCredentials();
      },
      error: (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error while removing a platform credential', error);
        this.notify(this.t('apiKeys.removeFailed', 'The key was not removed.'), NotificationTypes.Error);
      }
    });
  }

  /**
   * Probe the provider. If a candidate has been typed, that key is tested; otherwise the stored one
   * is. The label on the button changes to say which, so the result is never ambiguous.
   */
  public onTestConnection(provider: AiProviderCredential): void {
    const request = new TestCredentialRequest();
    request.Provider = provider.Key;

    if (this.hasCandidate(provider)) {
      request.Value = this.candidates[provider.CredentialKey].trim();
    }

    this.testing[provider.Key] = true;
    this.probeResults[provider.Key] = null as unknown as AiProbeResult;

    this.platformCredentialService.testConnection(request).subscribe({
      next: (response: ResponseObject) => {
        this.testing[provider.Key] = false;

        if (!response.Success) {
          this.probeResults[provider.Key] = {
            Ok: false, Provider: provider.Key, Model: null, ResponseTimeMs: null,
            Error: response.ResponseMessage || this.t('apiKeys.testFailed', 'The test could not be run.')
          };
          return;
        }

        this.probeResults[provider.Key] = response.ResponseData as AiProbeResult;
      },
      error: (error) => {
        this.testing[provider.Key] = false;
        console.error('Error while testing a provider connection', error);
        this.probeResults[provider.Key] = {
          Ok: false, Provider: provider.Key, Model: null, ResponseTimeMs: null,
          Error: this.t('apiKeys.testFailed', 'The test could not be run.')
        };
      }
    });
  }

  public onSelectTab(tab: string): void {
    this.activeTab = tab;

    // Load on first view rather than up front: an operator who came only to rotate a key should not
    // pay for a second round trip.
    if (tab === 'functions' && !this.functionsLoaded) {
      this.loadFunctions();
    }

    if (tab === 'health' && !this.healthLoaded) {
      this.loadHealth();
    }
  }

  /** Read the stored picture. Does not probe — that is the explicit "check now" button. */
  public loadHealth(): void {
    this.platformCredentialService.fetchHealth().subscribe({
      next: (response: ResponseObject) => {
        if (response.Success) {
          this.health = response.ResponseData as AiHealthReport;
          this.healthLoaded = true;
        }
      },
      error: (error) => console.error('Error while fetching AI provider health', error)
    });

    this.platformCredentialService.fetchSpend().subscribe({
      next: (response: ResponseObject) => {
        if (response.Success) {
          this.spend = response.ResponseData as AiSpendReport;
        }
      },
      error: (error) => console.error('Error while fetching AI spend', error)
    });
  }

  public onRunHealthCheck(): void {
    this.checkingHealth = true;

    this.platformCredentialService.runHealthCheck().subscribe({
      next: (response: ResponseObject) => {
        this.checkingHealth = false;

        if (!response.Success) {
          this.notify(response.ResponseMessage || this.t('apiKeys.healthCheckFailed', 'The check could not be run.'), NotificationTypes.Error);
          return;
        }

        this.health = response.ResponseData as AiHealthReport;
        this.healthLoaded = true;
      },
      error: (error) => {
        this.checkingHealth = false;
        console.error('Error while running the AI health check', error);
        this.notify(this.t('apiKeys.healthCheckFailed', 'The check could not be run.'), NotificationTypes.Error);
      }
    });
  }

  public loadFunctions(): void {
    this.ngxSpinnerService.show();

    this.platformCredentialService.fetchFunctions().subscribe({
      next: (response: ResponseObject) => {
        this.ngxSpinnerService.hide();

        if (!response.Success) {
          this.notify(response.ResponseMessage || this.t('apiKeys.functionsLoadFailed', 'Could not load the AI functions.'), NotificationTypes.Error);
          return;
        }

        this.functions = (response.ResponseData as AiFunctionView[]) ?? [];
        this.functionsLoaded = true;
      },
      error: (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error while fetching AI functions', error);
        this.notify(this.t('apiKeys.functionsLoadFailed', 'Could not load the AI functions.'), NotificationTypes.Error);
      }
    });
  }

  /** Persist an assignment. A blank provider clears it and the feature returns to its default. */
  public onSaveBinding(fn: AiFunctionView): void {
    const request = new SaveFunctionBindingRequest();
    request.FunctionKey = fn.Key;
    request.ProviderKey = fn.AssignedProviderKey || null;
    request.ModelId = fn.AssignedModel || null;
    request.IsEnabled = fn.IsEnabled;

    this.ngxSpinnerService.show();

    this.platformCredentialService.saveFunctionBinding(request).subscribe({
      next: (response: ResponseObject) => {
        this.ngxSpinnerService.hide();

        if (!response.Success) {
          // An impossible assignment is refused server-side. Reload so the screen shows what is
          // actually in force rather than the rejected choice.
          this.notify(response.ResponseMessage || this.t('apiKeys.bindingFailed', 'The assignment was not saved.'), NotificationTypes.Error);
          this.loadFunctions();
          return;
        }

        this.notify(this.t('apiKeys.bindingSaved', 'Assignment saved.'), NotificationTypes.Success);
        this.loadFunctions();
      },
      error: (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error while saving an AI function binding', error);
        this.notify(this.t('apiKeys.bindingFailed', 'The assignment was not saved.'), NotificationTypes.Error);
      }
    });
  }

  public onToggleAudit(): void {
    this.auditVisible = !this.auditVisible;

    if (!this.auditVisible) {
      return;
    }

    this.platformCredentialService.fetchAuditTrail().subscribe({
      next: (response: ResponseObject) => {
        this.auditTrail = response.Success ? (response.ResponseData as PlatformCredentialAudit[]) ?? [] : [];
      },
      error: (error) => {
        console.error('Error while fetching the credential audit trail', error);
        this.auditTrail = [];
      }
    });
  }

  /** Masked display for a stored credential — the only rendering of a key this screen does. */
  public maskedValue(provider: AiProviderCredential): string {
    if (provider.Status?.Last4) {
      return '••••••••' + provider.Status.Last4;
    }
    return '••••••••';
  }

  public sourceLabel(provider: AiProviderCredential): string {
    switch (provider.Status?.Source) {
      case 'Stored':
        return this.t('apiKeys.sourceStored', 'Stored (encrypted)');
      case 'Configuration':
        return this.t('apiKeys.sourceConfiguration', 'Server configuration file');
      default:
        return this.t('apiKeys.sourceNotConfigured', 'Not configured');
    }
  }

  /** Status pill styling. Three distinct states, three distinct colours — see sourceLabel. */
  public pillClass(provider: AiProviderCredential): string {
    switch (provider.Status?.Source) {
      case 'Stored':
        return 'is-stored';
      case 'Configuration':
        return 'is-config';
      default:
        return 'is-none';
    }
  }

  /** Overall health pill. Stale is coloured as loudly as Critical, deliberately. */
  public overallPillClass(): string {
    switch (this.health?.Overall) {
      case 'Healthy':
        return 'is-stored';
      case 'Degraded':
        return 'is-config';
      case 'Critical':
      case 'Stale':
        return 'is-fail';
      default:
        return 'is-none';
    }
  }

  public statusClass(status: string): string {
    switch (status) {
      case 'Healthy':
        return 'is-healthy';
      case 'Failing':
        return 'is-failing';
      case 'Stale':
        return 'is-stale';
      default:
        return 'is-none';
    }
  }

  /** A substitution means the operator is not getting what they asked for; it must not look normal. */
  public noteClass(fn: AiFunctionView): string {
    if (!fn.Resolution?.CanRun) {
      return 'is-fail';
    }
    return fn.Resolution.Source === 'Substitute' ? 'is-warn' : 'is-muted';
  }

  private t(key: string, fallback: string): string {
    const translated = this.transloco.translate(key);
    return translated === key ? fallback : translated;
  }

  private notify(message: string, type: NotificationTypes): void {
    this.notificationsService.showNotification(this.t('apiKeys.title', 'API Keys'), message, type);
  }
}
